<script>
  // external libraries
  import { onMount } from "svelte";

  // local storage & svelte-store
  import {
    getStoredData,
    data,
    getStoredSpecs,
    viewSpecs,
    dataInfo,
    activeSpec,
    getUnlockedSpecs,
    getActiveSpec,
    currentSpec,
    callQueue,
    colorHistory,
    getColorHistory,
    devicePreview,
    saveToLocalStorage,
    currMenu,
    systemLog,
    getStoredLog,
    directContext,
    getPreferences,
    loadRecStacks,
    currentElementLockGroup,
    menuConfig,
    loadClingo,
    loadPyodide_,
  } from "./editor/storage.js";

  // inetrnal imports
  import { setData } from "./editor/component/data.js";
  import { deviceTypes, moveToArtboard } from "./editor/component/artboard.js";
  import { firstMenuRoles, settingRoles } from "./editor/menuSchema.js";
  import {
    setExplorationMode,
    explorationMode,
    underHypothesis,
  } from "./editor/exploration/exploration.js";
  import { AugmentationStack } from "./editor/exploration/augment.js";
  import { closeContextMenu } from "./editor/utility.js";

  // subcomponents
  import DupoStyle from "./dupoStyle.svelte";
  import FirstLevelMenu from "./editorInterface/firstLevelMenu.svelte";
  import SettingMenu from "./editorInterface/settingMenu.svelte";
  import SecondLevelMenu from "./editorInterface/secondLevelMenu.svelte";
  import SettingPanel from "./editorInterface/settingPanel.svelte";
  import Artboard from "./editorInterface/artboardView.svelte";
  import ArtboardButton from "./editorInterface/component/artboardListButton.svelte";
  import MenuSearch from "./editorInterface/menuSearch.svelte";
  import ExplorationView from "./editorInterface/explorationView.svelte";
  import SystemTooltip from "./editorInterface/component/systemTooltip.svelte";
  import ContextMenu from "./editorInterface/component/contextMenu.svelte";
  import QuickEditView from "./editorInterface/component/quickEditView.svelte";
  import ExampleLoader from "./editorInterface/exampleLoader.svelte";

  // direct manipulation support
  import { clearHighlight } from "./editor/direct.js";
  import { afterRender } from "./editor/component/artboard.js";
  import StatusMarker from "./editorInterface/statusMarker.svelte";
  export let clingoPort;
  // first/setting menu control
  let hasFirstMenu = false;
  let hasSettingMenu = false;
  let isFirstMenuFull = false;
  function clickFirstMenu(event, role) {
    if ($currMenu === role) {
      currMenu.set(undefined);
    } else {
      currMenu.set(role);
    }
    closeThirdMenu();
    menuConfig.set(undefined);
    if (role !== "selection") {
      directContext.set(undefined);
      clearHighlight();
    }
  }
  function clickSettingMenu(event, role) {
    if ($currMenu === role) {
      currMenu.set(undefined);
    } else {
      currMenu.set(role);
    }
    closeThirdMenu();
    menuConfig.set(undefined);
    if (role !== "selection") {
      directContext.set(undefined);
      clearHighlight();
    }
  }
  function keyFirstMenu(event, role) {
    if ($currMenu !== role) {
      currMenu.set(role);
      closeThirdMenu();
      menuConfig.set(undefined);
      if (role !== "selection") {
        directContext.set(undefined);
        clearHighlight();
      }
    }
  }
  function keySettingMenu(event, role) {
    if ($currMenu !== role) {
      currMenu.set(role);
      closeThirdMenu();
      menuConfig.set(undefined);
      if (role !== "selection") {
        directContext.set(undefined);
        clearHighlight();
      }
    }
  }
  // keyboard actions
  let preventKeyboardAction = false;
  function preventKB() {
    preventKeyboardAction = true;
  }
  function allowKB() {
    preventKeyboardAction = false;
  }
  // third level
  let showThirdMenu = false;
  function openThirdMenu() {
    showThirdMenu = true;
  }
  function closeThirdMenu() {
    showThirdMenu = false;
  }
  // activity-related
  function updateCurrentSpec() {
    currentSpec.set($viewSpecs.filter((spec) => spec.uid === $activeSpec)[0]);
  }
  // rendering
  let specList = {};
  async function loadViews(specs) {
    specList = {};
    specs.forEach((spec) => {
      if (!specList[spec.artboard.screen_type])
        specList[spec.artboard.screen_type] = [];
      specList[spec.artboard.screen_type].push(spec);
    });
    await specs.forEach(async (spec) => {
      await spec.render($data, $dataInfo, () => {
        updateCurrentSpec();
        afterRender(spec);
      });
    });
    window.viewSpecs = specs;
  }
  viewSpecs.subscribe(async (specs) => {
    if (!$underHypothesis) {
      await loadViews(specs);
    }
  });
  // manage data & specs
  data.subscribe(async (d) => {
    if (d) {
      dataInfo.set(setData(d));
      await loadViews($viewSpecs);
    }
  });
  // preview setting
  devicePreview.subscribe(async (val) => {
    if (val === false) {
      currMenu.set(undefined);
      await loadViews($viewSpecs);
    }
  });
  // direct manipulation
  directContext.subscribe((c) => {
    if (c) {
      currMenu.set("selection");
    }
  });
  // exploration view
  let hasExploration = false;
  explorationMode.subscribe((mode) => {
    if (mode) hasExploration = true;
    else hasExploration = false;
    // cancel menu
    if (hasExploration) currMenu.set(undefined);
  });
  // source-target artboards for element locks

  // init: read cache;
  onMount(async () => {
    // load libraries
    await loadClingo();
    // takes time to load
    loadPyodide_();
    // load preferences;
    getPreferences();
    loadRecStacks();
    // load data from cache;
    data.set(getStoredData());
    // load specs from cache
    await getStoredSpecs();
    // load spec activity from cache
    let active = getActiveSpec();
    if (active !== null) {
      activeSpec.set(active);
      currentSpec.set(
        $viewSpecs.filter((spec) => spec.uid === active)[0] || {}
      );
    } else {
      activeSpec.set(-1);
    }
    // manage active spec
    activeSpec.subscribe((uid) => {
      let newCurrentSpec = $viewSpecs.filter((spec) => spec.uid === uid)[0];
      currentSpec.set(newCurrentSpec);
      if (!uid) {
        currMenu.set(undefined);
        currentElementLockGroup.set([]);
      }
      saveToLocalStorage("activeSpec", uid);
      if (uid) {
        let lockGroup = [uid];
        $viewSpecs.forEach((spec) => {
          if (
            spec.uid !== uid &&
            (spec.source === uid || newCurrentSpec?.source === spec.uid)
          )
            lockGroup.push(spec.uid);
        });
        currentElementLockGroup.set(lockGroup);
      }
    });
    // load spec locks from cache
    getUnlockedSpecs();
    // load system log
    await getStoredLog();
    $systemLog.log({ message: "System opened" });

    // handling first-level menus
    currMenu.subscribe((menu) => {
      if (!menu) {
        hasFirstMenu = false;
        hasSettingMenu = false;
        $systemLog.log({ message: "Unset first menu" });
      } else if (firstMenuRoles.includes(menu)) {
        hasFirstMenu = true;
        hasSettingMenu = false;
        isFirstMenuFull = menu === "data";
        $systemLog.log({ message: "Set first menu", menu });
      } else if (settingRoles.includes(menu)) {
        hasFirstMenu = false;
        hasSettingMenu = true;
        $systemLog.log({ message: "Set first menu", menu });
      }
      if (hasFirstMenu || hasSettingMenu) {
        setExplorationMode(undefined);
      }
    });

    // color history
    let ch = getColorHistory();
    if (ch == null) {
      colorHistory.set([]);
    } else {
      colorHistory.set(ch);
    }

    // view area position
    document.querySelector("#view-column").scroll({ left: 480 });

    // augmentation
    AugmentationStack.set({});
  });
  if ($activeSpec) {
    moveToArtboard($activeSpec);
  }
  // redo & undo;
  let pressedMeta = false,
    pressedShift = false;
  document.body.addEventListener("keydown", (event) => {
    if (!preventKeyboardAction) {
      if (event.key === "Meta") pressedMeta = true;
      else if (event.key === "Control") pressedMeta = true;
      if (event.key === "Shift") pressedShift = true;
      if (pressedMeta && !pressedShift && event.key === "z") {
        systemUndo();
        $systemLog.log({ message: "Redo (keyboard)" });
      } else if (
        pressedMeta &&
        pressedShift &&
        (event.key === "z" || event.key === "Z")
      ) {
        systemRedo();
        $systemLog.log({ message: "Undo (keyboard)" });
      }
    }
  });

  document.body.addEventListener("keyup", (event) => {
    if (event.key === "Meta") pressedMeta = false;
    else if (event.key === "Control") pressedMeta = false;
    if (event.key === "Shift") pressedShift = false;
  });

  document.body.addEventListener("click", (event) => {
    if (!event.target.getAttribute("data-contextmenu")) {
      closeContextMenu();
    }
  });

  function systemUndo() {
    callQueue.update((queue) => {
      queue.undo();
      return queue;
    });
  }
  function systemRedo() {
    callQueue.update((queue) => {
      queue.revokeImmediate();
      return queue;
    });
  }
  let showUndoList = false,
    showRedoList = false;
  callQueue.subscribe((queue) => {
    // access call queue
    window.callQueue = queue;
  });
</script>

<DupoStyle />

<div class="app-area" id="dupo-app">
  <div id="global-nav">
    <FirstLevelMenu
      clickFunction={clickFirstMenu}
      kbFunction={keyFirstMenu}
      {preventKeyboardAction}
    />
    <SettingMenu
      clickFunction={clickSettingMenu}
      kbFunction={keySettingMenu}
      {preventKeyboardAction}
    />
  </div>
  {#if hasFirstMenu}
    <div
      id="second-nav"
      class={(showThirdMenu ? "third" : "no-third") +
        (isFirstMenuFull ? " full" : "")}
    >
      <SecondLevelMenu
        inputFocusAction={preventKB}
        inputBlurAction={allowKB}
        {openThirdMenu}
        {closeThirdMenu}
      />
    </div>
  {/if}
  {#if !$devicePreview}
    <div
      id="view-column"
      class={"pt-2" +
        (showThirdMenu ? " third-nav-on" : "") +
        (hasFirstMenu && hasSettingMenu && !isFirstMenuFull
          ? " second-side-nav-on"
          : "") +
        (hasFirstMenu && !hasSettingMenu && !isFirstMenuFull
          ? " second-nav-on"
          : "") +
        (!hasFirstMenu && hasSettingMenu ? " side-nav-on" : "") +
        (hasFirstMenu && isFirstMenuFull ? " second-nav-full-on" : "")}
    >
      <div class="view-area">
        {#each Object.keys(deviceTypes) as dtype}
          {#if specList[dtype]}
            {#each specList[dtype] as spec}
              <div
                class={"dupo-view-wrapper" +
                  ($activeSpec === spec.uid ? " active" : "")}
                style="line-height: 100%;"
              >
                <h2 class="view-heading">
                  <i class={`bi bi-${deviceTypes[dtype]?.icon || "display"}`} />
                  {dtype}
                </h2>
                <Artboard
                  {spec}
                  inputFocusAction={preventKB}
                  inputBlurAction={allowKB}
                />
              </div>
            {/each}
          {/if}
        {/each}
      </div>
    </div>
  {/if}
  {#if hasSettingMenu}
    <div id="side-nav" class={$devicePreview ? "preview-mode" : ""}>
      <SettingPanel inputFocusAction={preventKB} inputBlurAction={allowKB} />
    </div>
  {/if}
  {#if hasExploration}
    <div id="exploration-view">
      <ExplorationView
        inputFocusAction={preventKB}
        inputBlurAction={allowKB}
        {clingoPort}
      />
    </div>
  {/if}

  <QuickEditView />

  <div id="virtual" />
</div>

<!-- view controls -->
<div
  id="view-control"
  class={(hasSettingMenu ? "pushed" : "") + (isFirstMenuFull ? " hidden" : "")}
>
  {#if !$devicePreview}
    <h4 class="view-heading">
      <i class="bi bi-easel-fill me-1" /> Artboards
    </h4>
    {#each Object.keys(deviceTypes) as dtype}
      {#if specList[dtype]}
        <div style="text-align: right;">
          {#each specList[dtype] as spec}
            <ArtboardButton
              {spec}
              icon={deviceTypes[dtype]?.icon || "display"}
              inputFocusAction={preventKB}
              inputBlurAction={allowKB}
            />
          {/each}
        </div>
      {/if}
    {/each}
  {/if}
</div>

<div class="redo-wrap">
  <!-- redo/undo -->
  <button
    type="button"
    on:click={() => {
      systemUndo();
      $systemLog.log({ message: "Undo (button)" });
    }}
    on:mouseover={() => (showUndoList = true)}
    on:focus={() => (showUndoList = true)}
    on:blur={() => (showUndoList = false)}
    on:mouseout={() => (showUndoList = false)}
    disabled={$callQueue?.processed?.length == 0}
    ><i class="bi bi-arrow-left-short" alt="undo" /></button
  >
  <button
    type="button"
    class="ms-1"
    on:click={() => {
      systemRedo();
      $systemLog.log({ message: "Redo (button)" });
    }}
    on:mouseover={() => (showRedoList = true)}
    on:focus={() => (showRedoList = true)}
    on:blur={() => (showRedoList = false)}
    on:mouseout={() => (showRedoList = false)}
    disabled={$callQueue?.canceled?.length == 0}
    ><i class="bi bi-arrow-right-short" alt="redo" /></button
  >
  {#if showUndoList}
    <div id="undo-list">
      {#if $callQueue?.processed?.length > 0}
        <div class="undo-list-wrap">
          {#each $callQueue?.processed as item}
            {#if item.message}
              <div class="undo-list-item">
                {item.message}
              </div>
            {:else if item.items}
              {#each Object.keys(item.items) as subitem}
                <div class="undo-list-item">
                  {item.items[subitem].description}
                </div>
              {/each}
            {:else if item.rules}
              {#each item.rules as subitem}
                <div class="undo-list-item">
                  {subitem.description}
                </div>
              {/each}
            {/if}
          {/each}
        </div>
      {/if}
      <div class="py-1" style="opacity: 0.7;">Undo edit (Ctrl/Cmd + z)</div>
    </div>
  {/if}

  {#if showRedoList}
    <div id="redo-list">
      {#if $callQueue?.canceled?.length > 0}
        <div class="undo-list-wrap">
          {#each $callQueue?.canceled as item}
            {#if item.message}
              <div class="undo-list-item">
                {item.message}
              </div>
            {:else if item.items}
              {#each Object.keys(item.items) as subitem}
                <div class="undo-list-item">
                  {item.items[subitem].description}
                </div>
              {/each}
            {:else if item.rules}
              {#each item.rules as subitem}
                <div class="undo-list-item">
                  {subitem.description}
                </div>
              {/each}
            {/if}
          {/each}
        </div>
      {/if}
      <div class="py-1" style="opacity: 0.7;">Redo edit (Ctrl/Cmd + z)</div>
    </div>
  {/if}
  <!-- search -->
  <MenuSearch inputFocusAction={preventKB} inputBlurAction={allowKB} />
  <ExampleLoader inputFocusAction={preventKB} inputBlurAction={allowKB} />
  <!-- pyodide, clingo status marker, link to github / manual -->
  <StatusMarker />
</div>

<SystemTooltip />
<ContextMenu />

<style>
  #dupo-app {
    overflow-y: hidden;
  }
  @media screen and (max-height: 760px) {
    #global-nav {
      width: 40px !important;
      justify-content: start;
      flex: 0 0 auto;
      height: 100%;
      overflow-y: scroll;
    }
  }
  @media screen and (min-height: 761px) {
    #global-nav {
      width: 40px !important;
      justify-content: space-between;
    }
  }
  #global-nav {
    width: 40px !important;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: #fafafa;
  }
  #second-nav {
    position: relative;
    display: flex;
    flex-direction: row;
    overflow-x: hidden;
    flex-wrap: wrap;
  }
  #second-nav.no-third {
    width: 240px !important;
  }
  #second-nav.third {
    width: 480px !important;
  }
  #second-nav.no-third.full {
    width: calc(100vw - 40px) !important;
  }
  #exploration-view {
    position: fixed;
    left: 40px;
    top: 32px;
    width: calc(100vw - 40px);
    height: calc(100vh - 32px);
    padding: 1rem;
    z-index: 3000;
    background-color: rgba(0, 0, 0, 0.3);
  }
  #view-column {
    background-color: #e4e4e4;
    width: calc(100vw - 40px); /* 40 */
    padding-bottom: 200px;
    overflow: scroll;
  }
  #view-column .view-area {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: start;
    width: calc(120vw + 1040px);
    padding-left: 520px;
    padding-right: 520px;
    left: 0;
  }
  #view-column.second-nav-on {
    width: calc(100vw - 280px); /* 40 + 240 */
  }
  #view-column.third-nav-on {
    width: calc(100vw - 520px); /* 40 + 240 + 240 */
  }
  #view-column.second-nav-on .view-area {
    left: -240px;
  }
  #view-column.third-nav-on .view-area {
    left: -480px;
  }
  #view-column.side-nav-on {
    width: calc(100vw - 280px); /* 40 + 240 */
  }
  #view-column.exploration-on {
    width: calc(100vw - 500px); /* 40 + 460 */
  }
  #view-column.exploration-on-glance {
    width: calc(20vw - 40px); /* 40 + 760 */
  }
  #view-column.second-nav-full-on {
    width: 0;
  }
  #side-nav {
    width: 240px !important;
  }
  #side-nav.preview-mode {
    width: calc(100vw - 40px) !important;
  }
  #second-nav,
  #view-column,
  #side-nav {
    flex: 0 0 auto;
    height: 100%;
  }
  #view-column,
  #side-nav {
    overflow-y: scroll;
  }
  #view-control {
    position: fixed;
    bottom: 15px;
    right: 15px;
    z-index: 999;
    width: auto;
    padding: 0.3rem;
  }
  #view-control.pushed {
    right: 255px;
  }
  #view-control.pushed-2 {
    right: 475px;
  }
  #view-control.pushed-2-glance {
    display: none;
  }
  #view-control.hidden {
    display: none;
  }
  #view-control .view-heading {
    padding: 0;
    margin: 0.5rem 0 0.5rem 0;
    font-size: 0.75rem;
    font-weight: var(--bold-font-weight);
    color: #757575;
    border: 0;
    background: transparent;
    text-align: right;
  }
  .dupo-view-wrapper {
    z-index: 10;
  }
  .dupo-view-wrapper.active {
    z-index: 30;
  }
  .redo-wrap {
    position: fixed;
    width: auto;
    min-width: 1100px;
    display: flex;
    height: 22px;
    top: 5px;
    left: 40px;
    line-height: 100%;
  }
  .redo-wrap button {
    appearance: none;
    border-radius: var(--default-radius);
    background-color: rgba(255, 255, 255, 0.5);
    padding: 3px 0.7rem 5px 0.7rem;
    height: 20px;
    border: 1px solid #dddddd;
    text-align: right;
    font-size: 12px;
    line-height: 100%;
  }
  .redo-wrap button:hover {
    font-weight: 500 !important;
    background-color: rgba(255, 255, 255, 0.75);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1), 0 3px 4px rgba(0, 0, 0, 0.05);
  }
  #undo-list,
  #redo-list {
    width: 220px;
    padding: 0.2rem 0.5rem;
    border-radius: var(--default-radius);
    background-color: white;
    border: 1px solid #dddddd;
    font-size: 0.8rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1), 0 3px 4px rgba(0, 0, 0, 0.05);
  }
  #undo-list {
    position: fixed;
    left: 40px;
    top: 35px;
  }
  #redo-list {
    position: fixed;
    left: 80.4px;
    top: 35px;
  }
  .undo-list-wrap {
    display: flex;
    flex-direction: column-reverse;
    height: auto;
  }
  .undo-list-item {
    display: flex;
    justify-content: space-between;
    flex-wrap: nowrap;
    padding: 0.25rem 0;
    border-bottom: 1px dashed #dddddd;
  }
  #virtual {
    width: 100vw;
    height: 0;
    overflow: hidden;
  }
</style>
